/**
 * /dongniai/platform_rule — 懂你AI课堂 平台规则说明 页
 *
 * 修复时间: 2026-09-17
 * 修复人: feishu-bot1 (Hermes 1号)
 *
 * 背景:
 *   price.html 价格页底部"查看全部"按钮硬编码 href="/dongniai/platform_rule/index.html",
 *   但 Next.js app 路由下从来没有 platform_rule 文件夹, 点击进去直接 404.
 *   老板 2026-09-17 报这个 bug → 选 B 方案: 新建本页面.
 *
 * 数据源:
 *   GET /api_faq.php → { code, rules: [{id,title,content,icon,sort,status}], faq, msg }
 *   rules 来自 fa_dongniai_platform_rule 表 (后台 KoOIsYnLPb.php/dongniai/platform_rule/index 可编辑)
 *
 * 与 FAQ 页的区别:
 *   - faq/page.tsx 是纯 server component, 数据 hardcode 在 TSX 里 (静态 GEO 优化用)
 *   - platform_rule 是 client component, 从 PHP API 动态拉 (因为后台可编辑, 必须实时)
 *
 * 样式: 跟 /faq 保持一致风格 (Tailwind + 面包屑 + 标题 + 列表 + 底部 CTA),
 *       老板以后想统一样式直接 grep className 即可.
 */

'use client';

import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, ListChecks, ArrowLeft } from 'lucide-react';

const SITE_URL = '/dongniai';
const PAGE_URL = `${SITE_URL}/platform_rule`;

interface Rule {
  id: string;
  title: string;
  content: string;
  icon?: string;
  sort?: string;
  status?: string;
}

export default function PlatformRulePage() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 注意: api_faq.php 是 ThinkPHP 公开 API, 不在 /dongniai/ 路由下, 直接 fetch 根路径
        const res = await fetch('/api_faq.php', { cache: 'no-store' });
        const json = await res.json();
        if (cancelled) return;
        if (json.code !== 1) {
          setError(json.msg || '加载失败');
          return;
        }
        // 只取 status=1 的, 按 sort 升序
        const activeRules = (json.rules || [])
          .filter((r: Rule) => String(r.status) === '1')
          .sort((a: Rule, b: Rule) => Number(a.sort || 0) - Number(b.sort || 0));
        setRules(activeRules);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '网络错误');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <article className="mx-auto max-w-4xl px-6 py-12">
        {/* 面包屑 */}
        <nav className="mb-6 text-sm text-slate-500" aria-label="breadcrumb">
          <ol className="flex flex-wrap items-center gap-1">
            <li>
              <a href={SITE_URL} className="hover:text-blue-600">
                首页
              </a>
            </li>
            <li aria-hidden>›</li>
            <li className="text-slate-700">平台规则</li>
          </ol>
        </nav>

        <header className="mb-10">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">
            平台规则说明
          </h1>
          <p className="mt-3 text-lg text-slate-600">
            懂你AI课堂（dongniai）的计费、扣费、套餐、充值余额等平台规则一目了然。
            {loading ? '正在加载…' : `共 ${rules.length} 条规则。`}
          </p>
        </header>

        {/* 加载态 */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="mr-2 size-5 animate-spin" />
            正在加载规则…
          </div>
        )}

        {/* 错误态 */}
        {error && !loading && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 size-5 shrink-0" />
              <div>
                <div className="font-semibold">加载规则失败</div>
                <div className="mt-1 text-sm">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* 空态 */}
        {!loading && !error && rules.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
            <ListChecks className="mx-auto mb-3 size-10 text-slate-400" />
            暂无平台规则说明
          </div>
        )}

        {/* 规则列表 */}
        {!loading && !error && rules.length > 0 && (
          <section aria-label="平台规则列表">
            <ol className="space-y-3">
              {rules.map((r, idx) => (
                <li
                  key={r.id}
                  className="rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-blue-300 hover:bg-blue-50/30"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className="flex size-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-slate-900">
                        {r.title}
                      </h3>
                      <p className="mt-1.5 leading-relaxed text-slate-700">
                        {r.content}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* 底部 CTA */}
        <section className="mt-12 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-center text-white">
          <h2 className="text-2xl font-bold">还有其他问题？</h2>
          <p className="mt-2 text-blue-100">
            查看常见问题，或直接联系懂你AI课堂客服。
          </p>
          <div className="mt-4 space-x-3">
            <a
              href={`${SITE_URL}/faq`}
              className="inline-block rounded-md bg-white px-6 py-2 font-semibold text-blue-600 hover:bg-blue-50"
            >
              查看 FAQ →
            </a>
            <a
              href={SITE_URL}
              className="inline-flex items-center gap-1 rounded-md border border-white px-6 py-2 font-semibold text-white hover:bg-white/10"
            >
              <ArrowLeft className="size-4" />
              返回首页
            </a>
          </div>
        </section>
      </article>
    </main>
  );
}